import { RestApiDeliveryMethodAreaDto, RestApiDeliveryMethodDto } from '@order-mage/integration-sdk';
import dayjs from 'dayjs';
import type { CartFormValues } from '@/entities/cart/model/cart-context';
import { isTimePlanActiveNow } from '@/entities/venue/lib/useTimeAvailability';
import { isPointInGeoJsonPolygon } from '@/shared/lib/geo';

/**
 * The moment a zone's time plan is judged at: the chosen slot of a timed order, otherwise now.
 * An order for tomorrow morning must not be refused because the zone is closed tonight.
 */
export const resolveDeliveryZoneTime = (time: CartFormValues['time'] | null | undefined) => {
    if (time?.type !== 'timed' || !time.timed?.date || !time.timed.timeStart) return undefined;
    const at = dayjs(`${time.timed.date}T${time.timed.timeStart}`);
    return at.isValid() ? at : undefined;
};

/** Single pass — no copy/sort. First match by lowest sortIndex among polygons active at `at` (default: now). */
export const matchAreaInMethod = (
    deliveryMethod: RestApiDeliveryMethodDto,
    coords: { lat: number; lon: number },
    at?: dayjs.Dayjs,
) => {
    if (!deliveryMethod.areas?.length) {
        return null;
    }

    let best: (typeof deliveryMethod.areas)[number] | null = null;
    for (const area of deliveryMethod.areas) {
        if (!isTimePlanActiveNow(area.restrictAvailableTime ?? [], at)) continue;
        if (!isPointInGeoJsonPolygon(coords, area.geoJson)) continue;
        if (!best || area.sortIndex < best.sortIndex) {
            best = area;
        }
    }
    return best;
};

/**
 * The area covering `coords` that its own time plan blocks right now.
 *
 * `matchAreaInMethod` filters these out, so "we don't deliver here at this hour" and
 * "we don't deliver here at all" both surface as a null match — and the customer reads
 * the temporary one as permanent. This is a separate lookup precisely so matching,
 * pricing and order gating keep their existing semantics; only the wording changes.
 */
export const matchTimeBlockedAreaInMethod = (
    deliveryMethod: RestApiDeliveryMethodDto,
    coords: { lat: number; lon: number },
    at?: dayjs.Dayjs,
): RestApiDeliveryMethodAreaDto | null => {
    if (!deliveryMethod.areas?.length) {
        return null;
    }

    let best: RestApiDeliveryMethodAreaDto | null = null;
    for (const area of deliveryMethod.areas) {
        // A zone the operator marked undeliverable is blocked for good, not until some
        // hour — describing it with a time window tells the customer to come back later
        // for delivery that will never come. `public: false` zones are internal, and
        // their plan names are not written for customers to read.
        if (area.deliveryAvailable === false) continue;
        if (area.public === false) continue;
        if (!area.restrictAvailableTime?.length) continue;
        if (isTimePlanActiveNow(area.restrictAvailableTime, at)) continue;
        if (!isPointInGeoJsonPolygon(coords, area.geoJson)) continue;
        if (!best || area.sortIndex < best.sortIndex) {
            best = area;
        }
    }
    return best;
};
